/**
 * Text creation flow, step 2: choose which words and phrases become cards, gaps
 * or both (handoff §4, §5, §19.3; ADR-115 Decisions 3, 4, 7 and 8).
 *
 * Two views over one set of marks: «В тексте», the running text where every word
 * is tappable, and «Списком», every distinct word in exactly one group. Switching
 * keeps every mark; a word picked in the list is the same card mark as the word in
 * the text. Roles are set only in the text; the list toggles mark / unmark.
 *
 * Where a gap occurs is the server's answer (POST /texts/flow/gaps), never searched
 * here: the chosen word shows as a gap at once, its other occurrences when the
 * answer arrives. If the request fails the marks stay as tapped, and the save still
 * validates on the server.
 *
 * In edit mode (ADR-115 Amendment 3) the same roles are offered, a new mark starts as
 * a gap, and a word or phrase whose card the receiving deck already holds (the
 * server's answer, POST /texts/{id}/carded) shows as such and can only be a gap. The
 * suggestion strip is there too: «Предложить слова» marks the server's pre-selection as
 * Cards, except the words the receiving deck already holds.
 */
import { useCallback, useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { useLocale } from '../i18n/context'
import { useIsDesktop } from '../hooks/useIsDesktop'
import { useCardedWords, useGapMatchSync, useTextLimits } from '../hooks/useTexts'
import { useTextEditStore } from '../stores/textEditStore'
import { useTextFlowStore } from '../stores/textFlowStore'
import { deckChoice, receivingDeckOf } from '../utils/textEdit'
import { GROUP_ORDER, groupWords, type ListGroup, type ListWord } from '../utils/textFlow'
import {
  activeSuggestions,
  addSuggestions,
  addableSuggestions,
  blockedWords,
  cardCount,
  clampRange,
  commonRole,
  dropSuggestions,
  gapCount,
  gapCover,
  hasCard,
  hasGap,
  hasRoleTargets,
  isWordGapForm,
  keyRole,
  looks,
  lostForms,
  makePhrase,
  phraseAt,
  phraseText,
  removePhrase,
  removeWord,
  resizePhrase,
  setAllRoles,
  setPhraseRole,
  setWordRole,
  shownOccurrences,
  submittedForms,
  wordItem,
  wordKey,
  wordRole,
  type BulkLimits,
  type Marks,
  type Role,
} from '../utils/textMarks'
import FlowFrame from '../components/texts/FlowFrame'
import MarkPanel, { type PanelView } from '../components/texts/MarkPanel'
import RunningText from '../components/texts/RunningText'
import { GhostButton, OutlinePill, PrimaryButton, Segmented } from '../components/texts/ui'

type View = 'text' | 'list'

type Panel =
  | { kind: 'idle' }
  | { kind: 'word'; index: number }
  | { kind: 'phrase'; id: string }
  | { kind: 'waiting'; anchor: number }
  | { kind: 'removed'; text: string; before: Marks; reselect: Panel }

function Chip({ word, role, onTap, suffix }: { word: ListWord; role: Role | null; onTap: () => void; suffix: string | null }) {
  const filled = role === 'card' || role === 'both'
  return (
    <button
      type="button"
      aria-pressed={role !== null}
      onClick={onTap}
      data-testid={`word-chip-${word.lemma}`}
      style={{
        minHeight: 36, padding: '0 12px', borderRadius: 18, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontFamily: 'var(--font-content)', fontSize: 15, color: 'var(--text-primary)',
        background: filled ? 'var(--cefr-b1-bg)' : 'var(--bg-surface)',
        border: filled ? '1px solid transparent' : '1px solid var(--border)',
      }}
    >
      <span
        style={role === 'gap' || role === 'both' ? {
          textDecorationLine: 'underline', textDecorationStyle: 'dashed', textDecorationColor: 'var(--accent)',
          textDecorationThickness: 2, textUnderlineOffset: 5,
        } : undefined}
      >
        {word.label}
      </span>
      {suffix && <span style={{ fontFamily: 'var(--font-ui)', fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)' }}>{suffix}</span>}
    </button>
  )
}

export default function TextFlowWordsPage() {
  const { tr } = useLocale()
  const tf = tr.textFlow
  const tw = tf.words
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  const flow = useTextFlowStore()
  // Edit mode's picker (/texts/:textId/edit/words): the same screen and roles, with no
  // suggestions and no list, returning its marks to the edit draft (handoff §15.3,
  // ADR-115 Amendment 3).
  const { textId } = useParams<{ textId: string }>()
  const edit = Boolean(textId)
  const editDraft = useTextEditStore((s) => (edit ? s.draft : null))
  // The deck the cards would go to, when it is known yet (chosen, or the default one).
  const deck = editDraft ? receivingDeckOf(deckChoice(editDraft)) : null
  const { analysis, marks, gapMatch, body } = flow
  const maxGaps = useTextLimits().data?.max_gaps ?? null
  const [view, setView] = useState<View>('text')
  const [open, setOpen] = useState<Set<ListGroup>>(new Set(['marked']))
  const [panel, setPanel] = useState<Panel>({ kind: 'idle' })
  // The last «Роль для всех»: «Вернуть» is offered while the marks are still what it made.
  const [bulk, setBulk] = useState<{ before: Marks; after: Marks; role: Role } | null>(null)

  const words = useMemo(() => analysis?.words ?? [], [analysis])
  const forms = useMemo(() => submittedForms(body, words, marks), [body, words, marks])
  const retryMatch = useGapMatchSync(body, forms, !!analysis)
  const shown = useMemo(() => shownOccurrences(body, words, marks, gapMatch), [body, words, marks, gapMatch])
  const cover = useMemo(() => gapCover(words, shown), [words, shown])

  // ── Already carded in the receiving deck (edit mode; the server's answer) ──
  const phraseSpans = useMemo(
    () => (edit ? marks.phrases.map((p) => ({ start: words[p.first].start, end: words[p.last].end })) : []),
    [edit, marks.phrases, words],
  )
  const deckId = edit ? deck?.id ?? null : null
  const cardedWordsQuery = useCardedWords(textId, deckId, body, null)
  const cardedPhrasesQuery = useCardedWords(textId, deckId, body, phraseSpans)
  const carded = useMemo(() => {
    const spans = new Set(
      [...(cardedWordsQuery.data?.words ?? []), ...(cardedPhrasesQuery.data?.words ?? [])].map((w) => `${w.start}-${w.end}`),
    )
    return {
      word: (i: number) => spans.has(`${words[i].start}-${words[i].end}`),
      phrase: (p: { first: number; last: number }) => spans.has(`${words[p.first].start}-${words[p.last].end}`),
      any: spans.size > 0,
    }
  }, [cardedWordsQuery.data, cardedPhrasesQuery.data, words])

  // The cards the receiving deck holds, as card keys: «Предложить слова» never marks them.
  const heldKeys = useMemo(() => {
    const keys = new Set<string>()
    words.forEach((w, i) => {
      const key = wordKey(w)
      if (key !== null && carded.word(i)) keys.add(key)
    })
    return keys
  }, [words, carded])
  // Until the deck's answer is in, the words it holds are not known: wait rather than suggest them.
  const heldPending = edit && deckId !== null && cardedWordsQuery.isPending

  const look = useMemo(() => {
    const base = looks(words, marks, cover)
    if (!deckId) return base
    return { ...base, words: base.words.map((l, i) => (carded.word(i) ? { ...l, carded: true } : l)) }
  }, [words, marks, cover, deckId, carded])

  const markedKeys = useMemo(() => {
    const keys = new Set(marks.cards)
    for (const g of marks.wordGaps) {
      const k = wordKey(words[g.word])
      if (k) keys.add(k)
    }
    return keys
  }, [marks, words])
  const groups = useMemo(() => (analysis ? groupWords(analysis, markedKeys) : null), [analysis, markedKeys])

  const cards = analysis ? cardCount(body, analysis, marks) : 0
  const gaps = gapCount(body, words, marks, gapMatch)
  const lost = lostForms(body, words, marks, gapMatch)

  // ── «Роль для всех»: one role for every mark ────────────────────────────────
  // A word the receiving deck holds stays gap only (edit mode, Amendment 3 Decision 5).
  const bulkLimits = useMemo<BulkLimits>(
    () => ({ cardlessWord: carded.word, cardlessPhrase: carded.phrase }),
    [carded],
  )
  const bulkTargets = useMemo(() => hasRoleTargets(words, marks), [words, marks])
  const bulkCommon = useMemo(() => commonRole(words, marks, cover), [words, marks, cover])
  // The gap roles that would take the text past the server's limit (never read here).
  const bulkOver = useMemo(() => {
    const over = (role: Role) => {
      if (maxGaps === null || !hasGap(role)) return false
      const after = gapCount(body, words, setAllRoles(words, marks, cover, role, bulkLimits), gapMatch)
      return after > maxGaps && after > gaps
    }
    return { card: false, gap: over('gap'), both: over('both') } as Record<Role, boolean>
  }, [maxGaps, body, words, marks, cover, bulkLimits, gapMatch, gaps])

  const blocked = useCallback(
    (opts: { anchor?: number; phrase?: string }) => blockedWords(words, marks, cover, opts),
    [words, marks, cover],
  )

  if (!analysis || !groups) return <Navigate to={edit ? `/texts/${textId}/edit` : '/texts/new'} replace />

  const setMarks = flow.setMarks
  const entry = flow.entry ?? 'essay'
  const title = entry === 'words' ? tf.entryWords : tf.entryEssay
  const back = () => navigate(edit ? `/texts/${textId}/edit` : '/texts/new', edit ? { replace: true } : undefined)
  // In edit mode every way out (‹, «Далее», the system back) lands the marks in the edit
  // draft: TextEditWordsPage does it when this screen unmounts, so no path can skip it.
  const next = () => (edit ? back() : navigate('/texts/new/save'))

  // A new mark's role: a Card in creation, a gap in edit mode (Card and Both are one
  // tap away); only a gap for a word with no card key.
  const newWordRole = (i: number): Role => (edit || wordKey(words[i]) === null ? 'gap' : 'card')
  const newPhraseRole: Role = edit ? 'gap' : 'card'

  /** Gaps the marks would have with one more form: the limit is the server's, read from /texts/limits. */
  const gapRoom = (adding: boolean) => {
    if (!adding || maxGaps === null || gaps < maxGaps) return true
    toast.error(tf.save.tooManyGaps(maxGaps))
    return false
  }

  // ── Intents from the text ──────────────────────────────────────────────────
  const phraseOwning = (form: string) => marks.phrases.find((p) => hasGap(p.role) && phraseText(body, words, p) === form)

  const finishPhrase = (anchor: number, target: number) => {
    const [first, last] = clampRange(anchor, target, blocked({ anchor }))
    if (first === last) return false
    const id = `p${Date.now().toString(36)}${first}`
    setMarks(makePhrase(words, marks, cover, first, last, id, newPhraseRole))
    setPanel({ kind: 'phrase', id })
    return true
  }

  const removeWithUndo = (text: string, nextMarks: Marks, reselect: Panel) => {
    setPanel({ kind: 'removed', text, before: marks, reselect })
    setMarks(nextMarks)
  }

  const onTap = (i: number, shift: boolean) => {
    if (panel.kind === 'waiting') {
      if (i === panel.anchor) setPanel({ kind: 'word', index: i })
      else finishPhrase(panel.anchor, i)
      return
    }
    if (shift && panel.kind === 'word' && panel.index !== i && !phraseAt(marks, i)) {
      if (finishPhrase(panel.index, i)) return
    }
    const p = phraseAt(marks, i)
    if (p) {
      if (panel.kind === 'phrase' && panel.id === p.id) {
        removeWithUndo(phraseText(body, words, p), removePhrase(marks, p.id), { kind: 'phrase', id: p.id })
      } else setPanel({ kind: 'phrase', id: p.id })
      return
    }
    // Another occurrence of a phrase's gap: the phrase is what the learner chose.
    const owner = cover.owner[i]
    if (owner !== null && !isWordGapForm(marks, owner)) {
      const owning = phraseOwning(owner)
      if (owning) setPanel({ kind: 'phrase', id: owning.id })
      return
    }
    const role = wordRole(words, marks, cover, i)
    if (role === null) {
      const first = newWordRole(i)
      if (!gapRoom(first === 'gap')) return
      setMarks(setWordRole(words, marks, cover, i, first))
      setPanel({ kind: 'word', index: i })
    } else {
      removeWithUndo(words[i].text, removeWord(words, marks, cover, i), { kind: 'word', index: i })
    }
  }

  /** A long-press released without moving: the tap path's waiting state, from that word. */
  const onPressRelease = (i: number) => {
    if (phraseAt(marks, i)) return onTap(i, false)
    if (wordRole(words, marks, cover, i) === null) {
      const first = newWordRole(i)
      if (!gapRoom(first === 'gap')) return
      setMarks(setWordRole(words, marks, cover, i, first))
    }
    setPanel({ kind: 'waiting', anchor: i })
  }

  const onPhrase = (first: number, last: number) => {
    const id = `p${Date.now().toString(36)}${first}`
    setMarks(makePhrase(words, marks, cover, first, last, id, newPhraseRole))
    setPanel({ kind: 'phrase', id })
  }

  const onResize = (id: string, first: number, last: number) => {
    setMarks(resizePhrase(marks, id, first, last))
    setPanel({ kind: 'phrase', id })
  }

  // ── The panel ─────────────────────────────────────────────────────────────
  const selectedPhrase = panel.kind === 'phrase' ? marks.phrases.find((p) => p.id === panel.id) ?? null : null
  const occurrencesOf = (form: string | null) => (form !== null ? shown.get(form)?.length ?? null : null)

  const panelView: PanelView = (() => {
    if (panel.kind === 'removed') return { kind: 'removed', text: panel.text }
    if (panel.kind === 'waiting') return { kind: 'waiting', text: words[panel.anchor].text }
    if (panel.kind === 'phrase' && selectedPhrase) {
      const text = phraseText(body, words, selectedPhrase)
      return {
        kind: 'phrase', text, words: selectedPhrase.last - selectedPhrase.first + 1, role: selectedPhrase.role,
        gapPlaces: hasGap(selectedPhrase.role) ? occurrencesOf(text) : null,
        cardedIn: deck && carded.phrase(selectedPhrase) ? deck.title : null,
      }
    }
    if (panel.kind === 'word') {
      const role = wordRole(words, marks, cover, panel.index)
      if (role === null) return { kind: 'idle' }
      const w = words[panel.index]
      const item = wordItem(analysis, w)
      const lemma = w.lemmas.length === 1 && w.lemmas[0].toLowerCase() !== w.text.toLowerCase() ? w.lemmas[0] : null
      return {
        kind: 'word', text: w.text, cefr: item?.cefr_level ?? null, lemma, translation: item?.translation ?? null,
        role, gapPlaces: hasGap(role) ? occurrencesOf(cover.owner[panel.index]) : null,
        gapOnly: wordKey(w) === null,
        cardedIn: deck && carded.word(panel.index) ? deck.title : null,
      }
    }
    return { kind: 'idle' }
  })()

  const onRole = (role: Role) => {
    if (hasCard(role) && (panel.kind === 'word' ? carded.word(panel.index) : selectedPhrase ? carded.phrase(selectedPhrase) : false)) return
    if (panel.kind === 'word') {
      const was = wordRole(words, marks, cover, panel.index)
      if (!gapRoom(hasGap(role) && !(was && hasGap(was)))) return
      setMarks(setWordRole(words, marks, cover, panel.index, role))
    } else if (selectedPhrase) {
      if (!gapRoom(hasGap(role) && !hasGap(selectedPhrase.role))) return
      setMarks(setPhraseRole(marks, selectedPhrase.id, role))
    }
  }
  const onRemove = () => {
    if (panel.kind === 'word') onTap(panel.index, false)
    else if (selectedPhrase) {
      removeWithUndo(phraseText(body, words, selectedPhrase), removePhrase(marks, selectedPhrase.id), { kind: 'phrase', id: selectedPhrase.id })
    }
  }
  const onRestore = () => {
    if (panel.kind !== 'removed') return
    setMarks(panel.before)
    setPanel(panel.reselect)
  }

  const applyBulk = (role: Role) => {
    if (bulkOver[role]) return
    const next = setAllRoles(words, marks, cover, role, bulkLimits)
    if (next === marks) return
    setPanel({ kind: 'idle' }) // a «Вернуть» of a single removal would undo this too
    setBulk({ before: marks, after: next, role })
    setMarks(next)
  }
  const undoBulk = () => {
    if (!bulk) return
    setMarks(bulk.before)
    setBulk(null)
  }

  // ── Suggestions (§4.3 «Снять» / «Предложить слова») ──────────────────────────
  const suggested = activeSuggestions(analysis, marks).length
  const addable = addableSuggestions(analysis, marks, cover, heldKeys).length
  const suggestionStrip = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, minHeight: 44 }}>
      <span style={{ fontSize: 13, lineHeight: 1.4, color: 'var(--text-secondary)' }} data-testid="suggestion-text">
        {suggested > 0 ? tw.suggestedOn(suggested) : tw.suggestedOff}
      </span>
      {suggested > 0 ? (
        <OutlinePill onClick={() => setMarks(dropSuggestions(analysis, marks))} data-testid="suggestions-drop" style={{ flexShrink: 0 }}>
          {tw.dropSuggested}
        </OutlinePill>
      ) : addable > 0 ? (
        <OutlinePill
          onClick={() => setMarks(addSuggestions(analysis, marks, cover, heldKeys))}
          disabled={heldPending}
          data-testid="suggestions-add"
          style={{ flexShrink: 0, opacity: heldPending ? 0.45 : 1 }}
        >
          {tw.addSuggested}
        </OutlinePill>
      ) : null}
    </div>
  )

  const overLimit = maxGaps !== null && (bulkOver.gap || bulkOver.both)
  const bulkRow = bulkTargets && (
    <div data-testid="bulk-roles" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 12, rowGap: 6 }}>
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{tw.bulk.label}</span>
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          <Segmented<Role>
            label={tw.bulk.label}
            inset={3}
            fit
            height={isDesktop ? 40 : 44}
            value={bulkCommon}
            onChange={applyBulk}
            options={(['card', 'gap', 'both'] as const).map((r) => ({
              id: r, label: tw.panel.roles[r], testId: `bulk-${r}`, disabled: bulkOver[r],
            }))}
          />
        </div>
      </div>
      {overLimit ? (
        <p role="status" data-testid="bulk-limit" style={{ margin: 0, fontSize: 13, lineHeight: 1.4, color: 'var(--text-secondary)' }}>
          {tw.bulk.tooMany(maxGaps)}
        </p>
      ) : bulk && marks === bulk.after ? (
        <div role="status" data-testid="bulk-applied" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
          <span>{tw.bulk.applied(tw.panel.roles[bulk.role])}</span>
          <GhostButton onClick={undoBulk} data-testid="bulk-undo" style={{ flexShrink: 0, height: 36 }}>{tw.panel.restore}</GhostButton>
        </div>
      ) : null}
    </div>
  )

  const statusLines = (flow.gapMatchFailed || lost.length > 0) && (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {flow.gapMatchFailed && (
        <div role="status" data-testid="gap-match-failed" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, lineHeight: 1.4, color: 'var(--text-secondary)' }}>
          <span style={{ flex: 1 }}>{tw.matchFailed}</span>
          <GhostButton onClick={retryMatch} style={{ flexShrink: 0, fontSize: 13 }}>{tw.retry}</GhostButton>
        </div>
      )}
      {lost.length > 0 && (
        <p role="status" data-testid="gap-lost" style={{ margin: 0, fontSize: 13, lineHeight: 1.4, color: 'var(--text-secondary)' }}>
          {tw.lost(lost.map((f) => `«${f}»`).join(', '))}
        </p>
      )}
    </div>
  )

  const runningText = (
    <RunningText
      body={body}
      words={words}
      wordLooks={look.words}
      sepLooks={look.seps}
      selectedWord={panel.kind === 'word' ? panel.index : null}
      selectedPhrase={selectedPhrase}
      waitingAnchor={panel.kind === 'waiting' ? panel.anchor : null}
      blocked={blocked}
      onTap={onTap}
      onPressRelease={onPressRelease}
      onPhrase={onPhrase}
      onResize={onResize}
      desktop={isDesktop}
    />
  )

  // ── The list (§5) ────────────────────────────────────────────────────────────
  const toggleOpen = (g: ListGroup) =>
    setOpen((prev) => {
      const nextOpen = new Set(prev)
      if (nextOpen.has(g)) nextOpen.delete(g)
      else nextOpen.add(g)
      return nextOpen
    })

  const list = (
    <div>
      <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-secondary)' }}>
        {tw.summary(analysis.counts.distinct)}
      </p>
      {analysis.counts.distinct === 0 && (
        <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{tw.empty}</p>
      )}
      {GROUP_ORDER.map((g) => {
        const groupItems = groups[g]
        if (groupItems.length === 0 && g !== 'marked') return null
        const isOpen = open.has(g)
        const info = tw.groups[g]
        return (
          <section key={g} data-testid={`group-${g}`} style={{ borderTop: '1px solid var(--border)' }}>
            <button
              type="button"
              aria-expanded={isOpen}
              onClick={() => toggleOpen(g)}
              style={{
                width: '100%', minHeight: 48, display: 'flex', alignItems: 'center', gap: 8,
                background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px',
                color: 'var(--text-primary)', fontFamily: 'var(--font-ui)', textAlign: 'left',
              }}
            >
              <span style={{ fontSize: 15, fontWeight: 600 }}>{info.name}</span>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }} data-testid={`group-count-${g}`}>{groupItems.length}</span>
              <span style={{ flex: 1 }} />
              {isOpen ? <ChevronUp size={20} aria-hidden color="var(--text-secondary)" /> : <ChevronDown size={20} aria-hidden color="var(--text-secondary)" />}
            </button>
            {isOpen && (
              <div style={{ padding: '0 4px 16px' }}>
                <p style={{ margin: '0 0 12px', fontSize: 13, lineHeight: 1.45, color: 'var(--text-secondary)' }}>{info.desc}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {groupItems.map((w) => {
                    const role = g === 'marked' ? keyRole(words, marks, w.key) : null
                    return (
                      <Chip
                        key={w.lemma}
                        word={w}
                        role={role}
                        suffix={role === 'gap' || role === 'both' ? tw.roleSuffix[role] : null}
                        onTap={() => (g === 'marked' ? flow.unmark(w.key) : flow.mark(w.key, w.label))}
                      />
                    )
                  })}
                </div>
              </div>
            )}
          </section>
        )
      })}
      <div style={{ borderTop: '1px solid var(--border)' }} />
    </div>
  )

  // ── Frame ─────────────────────────────────────────────────────────────────
  const switcher = (
    <Segmented<View>
      label={tw.viewSwitch}
      value={view}
      onChange={setView}
      width={isDesktop ? 280 : undefined}
      options={[
        { id: 'text', label: tw.viewText, testId: 'view-text' },
        { id: 'list', label: tw.viewList(analysis.counts.distinct), testId: 'view-list' },
      ]}
    />
  )

  const summary = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 13, color: 'var(--text-secondary)' }} data-testid="words-summary">
      <strong style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{tw.cards(cards)}</strong>
      <span>{gaps > 0 ? tw.gaps(gaps) : tw.noGaps}</span>
    </div>
  )

  const notice = view === 'list' && flow.notice && (
    <div
      role="status"
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, fontSize: 14, color: 'var(--text-secondary)' }}
    >
      <span>{tw.moved(flow.notice.label)}</span>
      <GhostButton onClick={flow.undoMove} style={{ flexShrink: 0 }}>{tw.undo}</GhostButton>
    </div>
  )

  const markPanel = (
    <MarkPanel
      view={panelView}
      desktop={isDesktop}
      onRole={onRole}
      onRemove={onRemove}
      onMakePhrase={() => panel.kind === 'word' && setPanel({ kind: 'waiting', anchor: panel.index })}
      onCancelWaiting={() => panel.kind === 'waiting' && setPanel({ kind: 'word', index: panel.anchor })}
      onRestore={onRestore}
      edit={edit}
    />
  )

  const meta = tw.meta(analysis.word_count, analysis.user_level)
  const cardedLegend = deck && carded.any && (
    <p style={{ margin: 0, fontSize: 12, lineHeight: 1.45, color: 'var(--text-tertiary)' }} data-testid="carded-legend">
      {tr.textEdit.cardedLegend(deck.title)}
    </p>
  )

  const mobile = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 16px 24px' }}>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }} data-testid="words-meta">{meta}</p>
      {edit ? null : switcher}
      {view === 'text' ? (
        <>
          {suggestionStrip}
          {edit && cardedLegend}
          {bulkRow}
          {statusLines}
          <div role="group" aria-label={tw.textLabel}>{runningText}</div>
        </>
      ) : (
        <>
          {bulkRow}
          {list}
        </>
      )}
    </div>
  )

  const desktop = (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: 24, padding: '24px 0' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {edit ? null : switcher}
          {view === 'text' ? <div style={{ flex: 1, minWidth: 0 }}>{suggestionStrip}</div> : <span style={{ flex: 1 }} />}
        </div>
        {view === 'text' && edit && cardedLegend}
        {bulkRow}
        {view === 'text' && statusLines}
        {view === 'text' ? (
          <div
            role="group"
            aria-label={tw.textLabel}
            style={{
              borderRadius: 16, padding: '24px 32px', background: 'var(--bg-surface)', border: '1px solid var(--border)',
              maxHeight: 'calc(100vh - 220px)', overflowY: 'auto',
            }}
          >
            {runningText}
          </div>
        ) : list}
      </div>
      <aside style={{ position: 'sticky', top: 24, alignSelf: 'start', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {view === 'text' ? (
          <>
            <div style={{ padding: 18, borderRadius: 16, background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
              {markPanel}
            </div>
            <p style={{ margin: 0, fontSize: 12, lineHeight: 1.45, color: 'var(--text-tertiary)' }}>{tw.panel.hintDesktop}</p>
          </>
        ) : notice && (
          <div style={{ padding: 14, borderRadius: 16, background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
            {notice}
          </div>
        )}
        <div
          style={{
            padding: 18, borderRadius: 16, background: 'var(--bg-surface)', border: '1px solid var(--border)',
            display: 'flex', flexDirection: 'column', gap: 14,
          }}
        >
          {summary}
          <PrimaryButton height={44} full onClick={next} data-testid="words-next-desktop">{tw.next}</PrimaryButton>
        </div>
      </aside>
    </div>
  )

  return (
    <FlowFrame
      step={edit ? undefined : 2}
      testId={edit ? 'text-edit-words' : undefined}
      title={edit ? tr.textEdit.pickerScreenTitle : title}
      leading="back"
      onLeave={back}
      backLabel={edit ? tr.textEdit.pickerBack : tf.stepShort(1)}
      subtitle={edit ? meta : `${tf.stepSubtitle(2, tf.stepNames.words)} · ${meta}`}
      maxWidth={isDesktop ? 1000 : 720}
      footer={
        isDesktop ? undefined : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {view === 'text' ? markPanel : notice}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              {summary}
              <PrimaryButton height={48} onClick={next} data-testid="words-next">{tw.next}</PrimaryButton>
            </div>
          </div>
        )
      }
    >
      {isDesktop ? desktop : mobile}
    </FlowFrame>
  )
}
